import type { Ref } from 'vue';
import { defineStore } from 'pinia';
import {
  computed,
  ref,
} from 'vue';

import type { Permissions } from '@modules/permissions/types';
import type { Permission } from '@modules/permissions/utils';
import { AccessDeniedError } from '@modules/core/errors';
import { useCurrentNamespace } from '@modules/permissions/composables';
import { containsPermission } from '@modules/permissions/utils';
import {
  assert,
} from '@utils/guards';

const usePermissionsStore = defineStore('permissions', () => {
  const permissions = ref<Permissions[]>();

  const { isAffiliateNamespace, isAdminNamespace } = useCurrentNamespace();

  let permissionsCache: Partial<Record<string, Ref<boolean>>> = {};

  function hasPermission(permission: Permission | Permission[]): Ref<boolean> {
    const cacheKey = permission.toString();

    if (!permissionsCache[cacheKey]) {
      permissionsCache[cacheKey] = computed(() => {
        if (Array.isArray(permission)) {
          return checkPermissionsMultiple(permission);
        }

        return checkPermissionsMultiple([permission]);
      });
    }

    const result = permissionsCache[cacheKey];
    assert(result);

    return result;
  }

  function checkPermissionsMultiple(permissionsToCheck: Permission[]): boolean {
    if (isAdminNamespace.value) {
      return permissionsToCheck.some((p) => containsPermission(p.getAdminPermission(), permissions.value));
    }

    if (isAffiliateNamespace.value) {
      return permissionsToCheck.some((p) => containsPermission(p.getAffiliatePermission(), permissions.value));
    }

    return permissionsToCheck.some((p) => containsPermission(p.getAnonymousPermission(), permissions.value));
  }

  function checkPermissionAsync(permission: Permission): Promise<void> {
    if (!hasPermission(permission).value) {
      return Promise.reject(new AccessDeniedError());
    }

    return Promise.resolve();
  }

  function setPermissions(value: Permissions[] | undefined): void {
    permissions.value = value;
    permissionsCache = {};
  }

  return {
    permissions,
    hasPermission,
    checkPermissionAsync,
    setPermissions,
  };
});

export default usePermissionsStore;
